import type {Theme, Components} from "../..";

type ChipOverrides = Components<Theme>["MuiChip"];

declare module "@mui/material/Chip" {
    interface ChipOwnProps {
        /**
         * If `true`, the chip will be rendered as selected.
         * NOTE: This is UI only. You will need to handle the selected state and
         * accessibility yourself!
         *
         * @example <Chip selected={isSelected} aria-selected={isSelected} />
         *
         * @default false
         */
        selected?: boolean;
    }
}

const overrides: ChipOverrides = {
    styleOverrides: {
        outlined: ({theme}) => ({
            borderWidth: 1.5,
            outline: `1px solid ${theme.color.border.subtle}`,
            outlineOffset: -1,
            borderColor: "transparent",
            color: theme.color.text.default,
        }),
        label: ({theme}) => ({
            ...theme.typography.title5,
        }),
        clickable: ({theme, ownerState}) => ({
            "&:hover, &.Mui-focusVisible": {
                borderWidth: 1.5,
                outline: "none",
            },

            "&:hover": {
                borderColor: ownerState.selected
                    ? theme.color.border.interactive
                    : theme.color.border.default,
                backgroundColor: theme.color.interaction.hovered,
            },

            "&.MuiChip-root:active": {
                backgroundColor: theme.color.interaction.pressed,
                boxShadow: theme.shadows[0],
            },
        }),
    },
    defaultProps: {
        variant: "outlined",
    },
    variants: [
        {
            props: {
                selected: true,
            },
            style: ({theme}) => ({
                "&.MuiChip-outlined": {
                    outline: "none",
                    borderWidth: 1.5,
                    borderColor: theme.color.border.interactive,
                },
            }),
        },
    ],
};

export default overrides;
